/**
 * ============================================================================
 * MAIN JAVASCRIPT ENTRY POINT
 * ============================================================================
 *
 * Main JavaScript file for custom Horizon theme extensions.
 * This file initializes all custom modules and components.
 *
 * @version 2.0.0
 * @author DeSight Studio GmbH
 *
 * ============================================================================
 */

// Note: All imports are removed for browser compatibility
// All classes are available globally after concatenation




/**
 * Main application class
 */
class App {
  constructor() {
    this.eventBus = new EventBus();
    this.modules = new Map();
    this.components = new Map();

    this.init();
  }

  /**
   * Initialize the application
   */
  init() {
    ready(() => {
      // Wait for custom theme settings to load
      if (window.CustomThemeSettings) {
        this.initWithSettings();
      } else {
        document.addEventListener('custom-theme:settings-loaded', () => {
          this.initWithSettings();
        });
      }
    });
  }

  /**
   * Initialize with theme settings
   */
  initWithSettings() {
    this.settings = window.CustomThemeSettings;

    if (window.Shopify && window.Shopify.designMode) {
      console.log('⚙️ Theme settings loaded:', this.settings);
    }

    this.initComponents();
    this.bindEvents();

    // Emit app ready event
    this.eventBus.emit('app:ready', { settings: this.settings });

    if (window.Shopify && window.Shopify.designMode) {
      console.log('🚀 DeSight Shopify X v1.0 initialized');
    }
  }

  /**
   * Initialize modules (bewusst leer gelassen, um JS auf das Notwendige zu reduzieren)
   */
  initModules() { }

  /**
   * Initialize components
   */
  initComponents() {
    // Custom buttons
    document.querySelectorAll('[data-custom-button]').forEach(element => {
      if (typeof CustomButton === 'function') {
        this.components.set(element, new CustomButton(element));
      }
    });

    // Product cards
    document.querySelectorAll('[data-product-card]').forEach(element => {
      if (typeof ProductCard === 'function') {
        this.components.set(element, new ProductCard(element));
      }
    });

    // Navigation menus
    document.querySelectorAll('[data-navigation-menu]').forEach(element => {
      if (typeof NavigationMenu === 'function') {
        this.components.set(element, new NavigationMenu(element));
      }
    });

    // Mega menu collection grids
    document.querySelectorAll('.mega-menu-collection-grid').forEach(element => {
      if (typeof MegaMenuCollectionGrid === 'function') {
        this.components.set(element, new MegaMenuCollectionGrid(element));
      }
    });



    // Collection product cards
    if (document.querySelectorAll('.main-collection').length > 0 && typeof initCollectionProductCards === 'function') {
      this.components.set('collectionProductCards', initCollectionProductCards());
    }

    // eTrusted Widget Styler
    if (typeof ETrustedWidgetStyler === 'function') {
      this.components.set('etrustedWidgetStyler', new ETrustedWidgetStyler());
    }

    // Mega menu scroll fix (commented out until implemented)
    // this.components.set('megaMenuScrollFix', new MegaMenuScrollFix());
  }

  /**
   * Bind global events
   */
  bindEvents() {
    // Handle window resize
    const resizeHandler = () => {
      this.eventBus.emit('window:resize');
    };
    window.addEventListener('resize', (typeof throttle === 'function') ? throttle(resizeHandler, 250) : resizeHandler);

    // Handle scroll
    const scrollHandler = () => {
      this.eventBus.emit('window:scroll');
    };
    window.addEventListener('scroll', (typeof throttle === 'function') ? throttle(scrollHandler, 100) : scrollHandler);

    // Handle theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', (e) => {
        this.eventBus.emit('theme:change', { isDark: e.matches });
      });
    }

    // Handle Shopify events
    document.addEventListener('shopify:section:load', (event) => {
      this.handleSectionLoad(event.detail.sectionId);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      this.handleSectionUnload(event.detail.sectionId);
    });
  }

  /**
   * Handle section load (for theme editor)
   */
  handleSectionLoad(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      // Re-initialize components in the loaded section
      this.initComponentsInSection(section);
      this.eventBus.emit('section:load', { sectionId, section });
    }
  }

  /**
   * Handle section unload (for theme editor)
   */
  handleSectionUnload(sectionId) {
    // Clean up components in the unloaded section
    this.cleanupComponentsInSection(sectionId);
    this.eventBus.emit('section:unload', { sectionId });
  }

  /**
   * Initialize components in a specific section
   */
  initComponentsInSection(section) {
    // Custom buttons
    section.querySelectorAll('[data-custom-button]').forEach(element => {
      if (!this.components.has(element) && typeof CustomButton === 'function') {
        this.components.set(element, new CustomButton(element));
      }
    });

    // Product cards
    section.querySelectorAll('[data-product-card]').forEach(element => {
      if (!this.components.has(element) && typeof ProductCard === 'function') {
        this.components.set(element, new ProductCard(element));
      }
    });

    // Navigation menus
    section.querySelectorAll('[data-navigation-menu]').forEach(element => {
      if (!this.components.has(element) && typeof NavigationMenu === 'function') {
        this.components.set(element, new NavigationMenu(element));
      }
    });

    // Mega menu collection grids
    section.querySelectorAll('.mega-menu-collection-grid').forEach(element => {
      if (!this.components.has(element) && typeof MegaMenuCollectionGrid === 'function') {
        this.components.set(element, new MegaMenuCollectionGrid(element));
      }
    });



    // Collection product cards
    if (section.querySelectorAll('.main-collection').length > 0 && typeof initCollectionProductCards === 'function') {
      if (!this.components.has('collectionProductCards')) {
        this.components.set('collectionProductCards', initCollectionProductCards());
      }
    }
  }

  /**
   * Clean up components in a specific section
   */
  cleanupComponentsInSection(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      // Find and destroy components in this section
      this.components.forEach((component, element) => {
        if (section.contains(element)) {
          if (typeof component.destroy === 'function') {
            component.destroy();
          }
          this.components.delete(element);
        }
      });
    }
  }

  /**
   * Get a module by name
   */
  getModule(name) {
    return this.modules.get(name);
  }

  /**
   * Get the event bus
   */
  getEventBus() {
    return this.eventBus;
  }

  /**
   * Initialize performance features based on settings
   */
  initPerformanceFeatures() {
    if (!this.settings) return;

    // Initialize Web Vitals tracking
    if (this.settings.features.webVitalsTracking && typeof initPerformanceMonitoring === 'function') {
      initPerformanceMonitoring();
    }

    // Initialize lazy loading
    if (this.settings.features.lazyLoading && typeof lazyLoadImages === 'function') {
      lazyLoadImages();
    }

    // Preload critical resources
    if (this.settings.features.preloadCriticalResources && typeof ResourceHints === 'function') {
      ResourceHints.preconnect('https://cdn.shopify.com');
      ResourceHints.dnsPrefetch('https://fonts.googleapis.com');
    }

    // Apply performance mode settings
    if (this.settings.features.performanceMode) {
      document.documentElement.classList.add('performance-mode');
    }
  }
}

// Initialize the application
const app = new App();

// Make app globally available for debugging and module access
if (typeof window !== 'undefined') {
  window.CustomThemeApp = app;
  window.DeSightApp = app; // Alternative reference for modules
}


